import { libraryTarget } from '../components/chat/libraryLinks';
import { authHeaders } from '../apiService';
import { selectionFromDocument } from './navigation';
import { ClassificationContext, classificationApiPrefix, classificationContextScope } from './transport';

export const BRIDGE_PROTOCOL = 'bark-classification-v1';
// Exact product APIs, not a general fetch proxy. The gateway enforces its own list and authorization.
const API_PATHS = new Set([
  'runtime', 'projects', 'projects/archive', 'personal-settings', 'bank-settings', 'review-scope',
  'contracts/amend-memo', 'contracts/document', 'contracts/extract', 'contracts/fact-model', 'contracts/fields',
  'contracts/guarantees', 'contracts/list', 'contracts/memo', 'contracts/prepare', 'contracts/property-location',
  'contracts/proposed-security', 'contracts/read', 'contracts/research-counterparty', 'contracts/save-common-facts',
  'contracts/save-structure-facts', 'contracts/brief', 'contracts/upload', 'deals/assistant', 'deals/classify-path', 'deals/common-accounts',
  'deals/component-borrower', 'deals/confirm-baseline-source', 'deals/confirm-funding', 'deals/for-contract',
  'deals/list', 'deals/notes', 'deals/notes/agent', 'deals/propose', 'deals/read', 'deals/repropose-option',
  'deals/source', 'deals/source-review', 'deals/summary', 'deals/update-summary', 'decision-passages',
  'decisions', 'decisions/history', 'default-capital', 'continue', 'agent', 'agent/cancel', 'agent/chat',
  'agent/context', 'agent/conversation', 'agent/current', 'agent/events', 'agent/review-input', 'agent/saved',
  'agent/start', 'allowance-review', 'baseline', 'eurlex-article', 'instrument', 'instrument/evidence',
  'instruments', 'loan-alternatives', 'loan-alternatives/history', 'node-documentation', 'node-source',
  'result-reference', 'structures/components', 'usage',
]);

export const MUTATION_PREFIX = 'bark:classification:mutation:';
export const SHARING_MUTATION_PREFIX = 'bark:classification:share-mutation:';
export function clearClassificationMutations() {
  try {
    for (let index = sessionStorage.length - 1; index >= 0; index -= 1) {
      const key = sessionStorage.key(index);
      if (key?.startsWith(MUTATION_PREFIX) || key?.startsWith(SHARING_MUTATION_PREFIX)) sessionStorage.removeItem(key);
    }
  } catch { /* Storage-disabled browsers never start retained mutations. */ }
}

export async function digest(value: string | ArrayBuffer): Promise<string> {
  const bytes = typeof value === 'string' ? new TextEncoder().encode(value) : value;
  const hash = await crypto.subtle.digest('SHA-256', bytes);
  return Array.from(new Uint8Array(hash), byte => byte.toString(16).padStart(2, '0')).join('');
}

export async function retainedMutation(context: ClassificationContext, request: WireRequest, headers: Record<string, string>) {
  const body = await digest(request.body || new ArrayBuffer(0));
  const fingerprint = await digest(JSON.stringify([...classificationContextScope(context), request.method, request.path, Object.entries(headers).sort(([a], [b]) => a.localeCompare(b)), body]));
  const key = MUTATION_PREFIX + fingerprint;
  try {
    let id = sessionStorage.getItem(key);
    if (!id || !/^[a-f0-9-]{20,64}$/.test(id)) {
      id = crypto.randomUUID(); sessionStorage.setItem(key, id);
    }
    return { key, id };
  } catch { throw new Error('Enable session storage before saving Classification changes.'); }
}

type WireRequest = { id: string; path: string; method: string; headers?: Record<string, string>; body?: ArrayBuffer };
export type ClassificationShellAction = { action: 'share-deal'; root_contract_id: string } | { action: 'saved-requests' } | { action: 'close-library' } | { action: 'open-library'; href: string; label: string; locator: string };

export function classificationLibraryAction(message: { [key: string]: unknown }): Extract<ClassificationShellAction, { action: 'open-library' }> | null {
  if (message.action !== 'open-library' || typeof message.href !== 'string' || message.href.length > 2048
    || typeof message.label !== 'string' || message.label.length > 300
    || typeof message.locator !== 'string' || message.locator.length > 300
    || !libraryTarget(message.href, 'https://library.invalid')?.article) return null;
  return { action: 'open-library', href: message.href, label: message.label, locator: message.locator };
}

export function attachClassificationBridge({ frame, base, context, onSelection, onReady, onDisconnect, onError, onCommandStateChange, onShellAction, libraryAvailable = false, referenceWidth, onAgentWidth }: {
  frame: HTMLIFrameElement; base: string; context: ClassificationContext;
  onSelection: (selection: string, replace: boolean, href: string) => void;
  onReady: () => void; onDisconnect: () => void; onError: (message: string) => void;
  onCommandStateChange?: () => void;
  onShellAction?: (action: ClassificationShellAction) => void;
  libraryAvailable?: boolean;
  referenceWidth?: () => number;
  onAgentWidth?: (width: number) => void;
}) {
  const darkTheme = () => [document.documentElement, document.body].some(element => element.getAttribute('data-theme') === 'dark');
  const origin = new URL(base).origin;
  const projectId = context.project_id;
  const scope = () => JSON.stringify(classificationContextScope(context));
  const capturedScope = scope();
  let channel = '';
  let documentNonce = '';
  let active = true;
  const requests = new Map<string, AbortController>();
  const deliveredMutations = new Map<string, { key: string; id: string; sha256: string; byteLength: number }>();
  const acknowledgments = new Map<string, AbortController>();
  const send = (payload: object, transfer: Transferable[] = []) => {
    if (active) frame.contentWindow?.postMessage({ protocol: BRIDGE_PROTOCOL, channel, ...payload }, origin, transfer);
  };
  const cancelAll = (keepAcknowledgments = false) => {
    requests.forEach(controller => controller.abort()); requests.clear(); deliveredMutations.clear();
    if (!keepAcknowledgments) { acknowledgments.forEach(controller => controller.abort()); acknowledgments.clear(); }
  };
  const acknowledge = async (requestId: string) => {
    const mutation = deliveredMutations.get(requestId);
    if (!mutation) return;
    deliveredMutations.delete(requestId);
    const controller = new AbortController();
    acknowledgments.set(requestId, controller);
    try {
      const headers = await authHeaders();
      if (!headers.Authorization) return;
      // A provisional stream header cannot prove that its durable job finished.
      // Query only after all bytes arrived and the verified frame acknowledged them.
      const result = await fetch(`${base}${classificationApiPrefix(context)}/commands/status?mutation_id=${encodeURIComponent(mutation.id)}`, {
        headers, signal: controller.signal, credentials: 'omit', cache: 'no-store', redirect: 'error',
      });
      if (!result.ok) return;
      const receipt = await result.json();
      if (active && !controller.signal.aborted && scope() === capturedScope
        && ['completed', 'rejected'].includes(receipt?.phase) && sessionStorage.getItem(mutation.key) === mutation.id) {
        if (receipt.response_sha256 === mutation.sha256 && receipt.response_byte_length === mutation.byteLength) {
          sessionStorage.removeItem(mutation.key);
        }
      }
    } catch { /* Pending jobs, lost acknowledgments and unavailable status retain the opaque ID. */ }
    finally { acknowledgments.delete(requestId); if (active) onCommandStateChange?.(); }
  };
  const relay = async (request: WireRequest) => {
    const controller = new AbortController();
    requests.set(request.id, controller);
    try {
      const parsed = new URL(request.path, 'https://classification.invalid');
      const path = parsed.pathname.startsWith('/api/') ? parsed.pathname.slice(5) : '';
      if (parsed.origin !== 'https://classification.invalid' || !API_PATHS.has(path) || !['GET', 'POST'].includes(request.method)
        || parsed.hash || (request.body !== undefined && !(request.body instanceof ArrayBuffer))) throw new Error('Unsupported Classification request.');
      const headers: Record<string, string> = {};
      for (const [name, value] of Object.entries(request.headers || {})) {
        if (['content-type', 'x-file-name'].includes(name.toLowerCase()) && typeof value === 'string') headers[name.toLowerCase()] = value;
      }
      const mutation = request.method === 'POST' ? await retainedMutation(context, request, headers) : undefined;
      if (mutation) headers['X-Classification-Mutation-Id'] = mutation.id;
      Object.assign(headers, await authHeaders());
      if (!headers.Authorization) throw new Error('Your session has expired. Sign in again.');
      const response = await fetch(`${base}${classificationApiPrefix(context)}/${path}${parsed.search}`, {
        method: request.method, headers, signal: controller.signal, body: request.method === 'GET' ? undefined : request.body,
        credentials: 'omit', cache: 'no-store', redirect: 'error',
      });
      const responseHeaders: Record<string, string> = {};
      for (const name of ['content-type', 'content-disposition', 'etag', 'retry-after']) {
        const value = response.headers.get(name); if (value) responseHeaders[name] = value;
      }
      if (controller.signal.aborted) return;
      const received: Uint8Array[] = [];
      let receivedLength = 0;
      send({ type: 'response', id: request.id, status: response.status, headers: responseHeaders });
      if (response.body) {
        const reader = response.body.getReader();
        try {
          while (active && !controller.signal.aborted) {
            const { done, value } = await reader.read();
            if (done) break;
            if (mutation) { received.push(value.slice()); receivedLength += value.byteLength; }
            const chunk = value.slice().buffer;
            send({ type: 'chunk', id: request.id, chunk }, [chunk]);
          }
        } finally { reader.releaseLock(); }
      }
      if (!controller.signal.aborted) {
        // Only the dispatcher can declare a complete authoritative command outcome.
        // Missing status, interruption and ambiguous failures retain the same operation ID.
        if (mutation) {
          const bytes = new Uint8Array(receivedLength);
          let offset = 0;
          for (const part of received) { bytes.set(part, offset); offset += part.byteLength; }
          const sha256 = await digest(bytes.buffer);
          if (controller.signal.aborted || !active) return;
          deliveredMutations.set(request.id, { ...mutation, sha256, byteLength: receivedLength });
        }
        send({ type: 'end', id: request.id });
      }
    } catch (error) {
      if (!controller.signal.aborted) send({ type: 'error', id: request.id, message: error instanceof Error ? error.message : 'Classification request failed.' });
    } finally {
      if (requests.get(request.id) === controller) requests.delete(request.id);
      if (request.method === 'POST' && active) onCommandStateChange?.();
    }
  };
  const receive = (event: MessageEvent) => {
    if (!active || event.source !== frame.contentWindow || event.origin !== origin || event.data?.protocol !== BRIDGE_PROTOCOL) return;
    const message = event.data;
    if (message.type === 'ready') {
      const selection = selectionFromDocument(message.href, base);
      if (selection === null || typeof message.nonce !== 'string' || !/^[a-f0-9-]{20,64}$/.test(message.nonce)) {
        onError('The Classification document could not be verified.'); return;
      }
      cancelAll(true);
      documentNonce = message.nonce;
      channel = crypto.randomUUID();
      send({ type: 'init', nonce: message.nonce, projectId, libraryAvailable, dark: darkTheme(), referenceWidth: referenceWidth?.() || 0, dealScope: context.deal_scope ? {
        share_id: context.deal_scope.share_id, root_contract_id: context.deal_scope.root_contract_id,
      } : null });
      onSelection(selection, false, message.href);
      onReady();
      return;
    }
    if (!channel || message.channel !== channel) return;
    if (message.type === 'agent-layout' && Number.isInteger(message.width) && message.width >= 0 && message.width <= 800) {
      onAgentWidth?.(message.width);
    } else if (message.type === 'shell-action' && message.nonce === documentNonce) {
      if (message.action === 'open-library') { const action = classificationLibraryAction(message); if (action) onShellAction?.(action); }
      else if (message.action === 'saved-requests' || message.action === 'close-library') onShellAction?.({ action: message.action });
      else if (message.action === 'share-deal' && typeof message.root_contract_id === 'string'
        && /^[a-f0-9]{32}$/.test(message.root_contract_id)) onShellAction?.({ action: 'share-deal', root_contract_id: message.root_contract_id });
    } else if (message.type === 'selection') {
      const selection = selectionFromDocument(message.href, base);
      if (selection !== null) onSelection(selection, message.replace === true, message.href);
    } else if (message.type === 'request' && typeof message.id === 'string' && /^[a-f0-9-]{20,64}$/.test(message.id)
      && typeof message.path === 'string' && typeof message.method === 'string' && !requests.has(message.id)) {
      void relay(message);
    } else if (message.type === 'received') {
      void acknowledge(message.id);
    } else if (message.type === 'cancel') {
      requests.get(message.id)?.abort(); requests.delete(message.id);
    } else if (message.type === 'dispose') {
      cancelAll(true); channel = ''; documentNonce = ''; onDisconnect();
    }
  };
  const themeObserver = new MutationObserver(() => { if (channel) send({ type: 'theme', dark: darkTheme() }); });
  for (const element of [document.documentElement, document.body]) themeObserver.observe(element, { attributes: true, attributeFilter: ['data-theme'] });
  window.addEventListener('message', receive);
  return Object.assign(() => { send({ type: 'dispose' }); active = false; themeObserver.disconnect(); cancelAll(); window.removeEventListener('message', receive); }, {
    setReferenceWidth: (width: number) => {
      if (channel && Number.isInteger(width) && width >= 0 && width <= 1000) send({ type: 'layout', referenceWidth: width });
    },
  });
}
